import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import { useNavigate } from "react-router";
import Typography from "@mui/material/Typography";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import DashboardLayout from "../layouts/dashboard";
import Alert from "@mui/material/Alert";
import Skeleton from "@mui/material/Skeleton";
import { ProviderSyncIssues, useSyncIssues } from "../../api/syncissues";
import { FadeIn } from "../../interface/shared/animations";

const CONTENT_PREVIEW_LENGTH = 120;

function truncateContent(content: string) {
    if (content.length <= CONTENT_PREVIEW_LENGTH) {
        return content;
    }
    return `${content.slice(0, CONTENT_PREVIEW_LENGTH)}…`;
}

function formatDate(date: Date) {
    if (Number.isNaN(date.getTime())) {
        return "—";
    }
    return date.toLocaleString();
}

export default function SyncIssues() {
    return (
        <DashboardLayout>
            <ProviderSyncIssues>
                <SyncIssuesContent />
            </ProviderSyncIssues>
        </DashboardLayout>
    );
}

function SyncIssuesContent() {
    const {
        syncIssues,
        errors,
        loading,
        fetchSyncIssues,
        page,
        perPage,
        total,
        setPage,
        setPerPage,
    } = useSyncIssues();
    const navigate = useNavigate();

    return (
        <Box sx={{ px: { xs: 2, md: 4 }, py: 3 }}>
            <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                alignItems={{ xs: "flex-start", sm: "center" }}
                justifyContent="space-between"
                sx={{ mb: 3 }}
            >
                <Box>
                    <Typography variant="h5" gutterBottom>
                        Sync issues
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Sync issues reported from warehouse devices.
                    </Typography>
                </Box>
            </Stack>
            {errors.length > 0 && (
                <Alert
                    severity="error"
                    sx={{ mb: 2 }}
                    action={
                        <Button color="inherit" size="small" onClick={() => fetchSyncIssues()}>
                            Retry
                        </Button>
                    }
                >
                    {errors.join(" ")}
                </Alert>
            )}
            <Card variant="outlined">
                <CardContent>
                    {loading ? (
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Date</TableCell>
                                    <TableCell>Type</TableCell>
                                    <TableCell>User</TableCell>
                                    <TableCell>Content</TableCell>
                                    <TableCell align="right"></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {Array.from({ length: 5 }).map((_, i) => (
                                    <TableRow key={i}>
                                        <TableCell>
                                            <Skeleton variant="text" />
                                        </TableCell>
                                        <TableCell>
                                            <Skeleton variant="text" />
                                        </TableCell>
                                        <TableCell>
                                            <Skeleton variant="text" />
                                        </TableCell>
                                        <TableCell>
                                            <Skeleton variant="text" />
                                        </TableCell>
                                        <TableCell align="right">
                                            <Skeleton variant="rounded" width={70} height={30} />
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    ) : (
                        <FadeIn>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Date</TableCell>
                                        <TableCell>Type</TableCell>
                                        <TableCell>User</TableCell>
                                        <TableCell>Content</TableCell>
                                        <TableCell align="right"></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {syncIssues.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={5}>
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                    sx={{ py: 4, textAlign: "center" }}
                                                >
                                                    No sync issues found.
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        syncIssues.map((issue) => (
                                            <TableRow key={issue.id}>
                                                <TableCell sx={{ whiteSpace: "nowrap" }}>
                                                    {formatDate(issue.date)}
                                                </TableCell>
                                                <TableCell>{issue.type}</TableCell>
                                                <TableCell>{issue.user.name}</TableCell>
                                                <TableCell sx={{ maxWidth: 480 }}>
                                                    <Typography
                                                        variant="body2"
                                                        color="text.secondary"
                                                        noWrap
                                                        title={issue.content}
                                                    >
                                                        {truncateContent(issue.content)}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Button
                                                        variant="outlined"
                                                        size="small"
                                                        endIcon={<VisibilityRoundedIcon fontSize="small" />}
                                                        onClick={() => navigate(`/sync-issues/${issue.id}`)}
                                                    >
                                                        View
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                            <TablePagination
                                component="div"
                                count={total}
                                page={page - 1}
                                onPageChange={(_, newPage) => setPage(newPage + 1)}
                                rowsPerPage={perPage}
                                onRowsPerPageChange={(e) => {
                                    setPerPage(parseInt(e.target.value, 10));
                                    setPage(1);
                                }}
                                rowsPerPageOptions={[10, 15, 25, 50]}
                            />
                        </FadeIn>
                    )}
                </CardContent>
            </Card>
        </Box>
    );
}
